import assessmentReader from '../store/readHelpers/AssessmentReader';
import globalsService from '../services/GlobalsService';
import HypothesisService from '../services/HypothesisService';
import uiService from '../services/UIService';

import liveDataLoader from './liveDataLoader';
import navigationService from '../services/NavigationService';
import postMessageService from '../services/PostMessageService';
import rootActions from '../actions/RootActions';
import sessionActions from '../actions/SessionActions';
import sessionReader from '../store/readHelpers/SessionReader';
import sessionParameterService from '../services/sessionParameters/SessionParameterService';
import storeManager from '../store/StoreManager';
import scorableItemReader from '../store/readHelpers/ScorableItemReader';
import tocReader from '../store/readHelpers/TocReader';
import backgroundAudioService from '../services/BackgroundAudioService';
import i18nService from '../services/i18nService';

const dynamicDataLoader = {
    startListeningForMessages() {
        /* istanbul ignore next */
        window.addEventListener('message', message => dynamicDataLoader.handleMessage(message));
    },

    /**
     * The message.data.params object can contain any session parameters, that would otherwise be passed as url parameters.
     * @param {object} message
     */
    handleMessage(message) {
        if (message.data.type === 'dynamic-player-data') {
            dynamicDataLoader.show(message.data.params);
        }
    },

    init() {
        globalsService.setTestPlayerReady();
        dynamicDataLoader.startListeningForMessages();
        postMessageService.postTo(window.parent, 'player-ready');

        const { UI_LANGUAGE_OVERRIDE } = window.marketplaceProperties;
        if (UI_LANGUAGE_OVERRIDE) {
            localStorage.setItem('uiOverrideLanguage', UI_LANGUAGE_OVERRIDE);
        }
    },

    needsReload({ assessmentRunId, contentItemId, contentRevisionId,
        sectionStimulusRevisionID, context, mode, partyId, viewAs, printParams = {}, layoutParams }) {
        if (
            !assessmentReader.isAssessmentLoaded()
            || sessionReader.getAssessmentRunId() !== assessmentRunId
            || sessionReader.getPartyId() !== partyId
            || sessionReader.getMode() !== mode
            || sessionReader.getContext() !== context
            || sessionReader.getContentItemId() !== contentItemId
            || sessionReader.getContentRevisionId() !== contentRevisionId
            || sessionReader.getPrintParam('type') !== printParams.type
            || sessionReader.isViewAsCandidate() !== sessionReader.isViewAsCandidate(viewAs)
            || JSON.stringify(sessionReader.getLayoutParams()) !== JSON.stringify(layoutParams)
            || sessionReader.getSectionStimulusRevisionId() !== sectionStimulusRevisionID
        ) {
            return true;
        }

        return false;
    },

    navigate(path) {
        if (path) {
            return navigationService.navigateToPath(path);
        }

        return navigationService.navigateToStartingPoint();
    },

    async reload(params) {
        const { path, scorableItem, sectionId, sectionStimulusRevisionID } = params;
        const parameters = { ...params };
        const oldType = sessionReader.getPrintParam('type');
        const typeChanged = params.printParams && params.printParams.type !== oldType;
        parameters.dynamicLoading = true;
        parameters.sectionStimulusRevisionID = sectionStimulusRevisionID;

        storeManager.dispatch(sessionActions.setAppReady(false));
        storeManager.dispatch(rootActions.reset());
        sessionParameterService.setSessionParams(parameters);
        uiService.setCurrentMarketplaceTheme();
        i18nService.changeLanguage(sessionReader.getLanguage()); // To be sure that language was changed after store was reloaded
        await liveDataLoader.loadDataForContext();

        if (sessionReader.isModeSection()
            && assessmentReader.hasBackgroundAudioResource()) {
            backgroundAudioService.playAudioForNonChildTest();
        }

        // Do the navigation before setting appReady, since the hashHistory needs to be set correctly before the route renders.
        if (scorableItem) {
            const scorable = scorableItemReader.getScorableItemByNumber(scorableItem);
            if (scorable) {
                const sectId = tocReader.getSectionIdForQuestionId(scorable.contentRevisionId);
                navigationService.navigateToScorableItem(sectId, scorable.contentRevisionId, scorableItem);
            }
        } else if (sectionId) {
            navigationService.navigateToSection(sectionId);
        } else {
            this.navigate(path);
        }

        storeManager.dispatch(sessionActions.setAppReady(true));

        if (postMessageService.isPostMessageSupported()) {
            if (typeChanged) {
                const hypothesis = document.querySelector('.h-sidebar-iframe');
                if (hypothesis) {
                    postMessageService.postTo(hypothesis.contentWindow, { action: 'test-player:updated' });
                }
            }

            postMessageService.postTo(window.parent, 'player-ready', '*');
        }
    },

    async show(params) {
        const { questionId, contentRevisionId, path, scorableItem } = params;
        const parameters = { ...params };
        parameters.dynamicLoading = true;

        if (questionId && !contentRevisionId) {
            parameters.contentRevisionId = questionId;
        }

        if (dynamicDataLoader.needsReload(parameters)) {
            return dynamicDataLoader.reload(params);
        }

        if (scorableItem) {
            const state = storeManager.getState();
            const { currentlyShown } = state;
            if (currentlyShown.scorableItem !== scorableItem) {
                const scorable = scorableItemReader.getScorableItemByNumber(scorableItem);
                if (scorable) {
                    const sectId = tocReader.getSectionIdForQuestionId(scorable.contentRevisionId);
                    navigationService.navigateToScorableItem(sectId, scorable.contentRevisionId, scorableItem);
                }
            }
        }

        if (sessionReader.isModeSection()
            && assessmentReader.hasBackgroundAudioResource()) {
            backgroundAudioService.playAudioForNonChildTest();
        }

        parameters.appReady = true;
        sessionParameterService.setSessionParams(parameters);
        const didNavigate = this.navigate(path);
        if (!didNavigate && sessionReader.isAnnotationMode()) {
            HypothesisService.appendDCMIMetaDataTagsAndInitHypothesisIfNeeded();
        }
        return true;
    },

};

export default dynamicDataLoader;
